import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  TextInput,
  Animated,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { getCurrencySymbol } from '../utils/currencies';
import { businessCurrency } from '../utils/businessDefaults';

interface ProductScannerScreenProps {
  catalogueId?: string;
}

type ScanState = 'idle' | 'scanning' | 'found' | 'not_found' | 'error';

// Device types that can scan barcodes
const SCANNER_CATEGORIES = ['scanner', 'camera'];

// ─── Device Picker Modal ──────────────────────────────────────────────────────

interface DevicePickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (device: any | null) => void; // null = device camera
  devices: any[];
  loading: boolean;
}

const DEVICE_ICONS: Record<string, string> = {
  scanner: 'barcode-outline',
  camera: 'camera-outline',
  other: 'hardware-chip-outline',
};

const SCANNER_TYPE_LABELS: Record<string, string> = {
  pos: 'POS / Handheld',
  a4: 'A4 Flatbed',
};

function DevicePicker({ visible, onClose, onSelect, devices, loading }: DevicePickerProps) {
  const pickerStyles = useThemedStyles(getPickerStyles);
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={pickerStyles.overlay}>
        <View style={pickerStyles.sheet}>
          <View style={pickerStyles.handle} />
          <View style={pickerStyles.header}>
            <Text style={pickerStyles.title}>Choose Scanner</Text>
            <TouchableOpacity onPress={onClose} style={pickerStyles.closeBtn}>
              <Ionicons name="close" size={22} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={pickerStyles.list}>
            {/* Built-in device camera (mobile only) */}
            {Platform.OS !== 'web' && (
              <TouchableOpacity style={pickerStyles.deviceRow} onPress={() => onSelect(null)} activeOpacity={0.7}>
                <View style={[pickerStyles.deviceIcon, { backgroundColor: '#6366F115' }]}>
                  <Ionicons name="camera-outline" size={22} color="#6366F1" />
                </View>
                <View style={pickerStyles.deviceInfo}>
                  <Text style={pickerStyles.deviceName}>Device Camera</Text>
                  <Text style={pickerStyles.deviceSub}>Built-in camera scanner</Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
              </TouchableOpacity>
            )}

            {/* Manual entry (always available) */}
            <TouchableOpacity style={pickerStyles.deviceRow} onPress={() => onSelect({ _isManual: true })} activeOpacity={0.7}>
              <View style={[pickerStyles.deviceIcon, { backgroundColor: '#F59E0B15' }]}>
                <Ionicons name="pencil-outline" size={22} color="#F59E0B" />
              </View>
              <View style={pickerStyles.deviceInfo}>
                <Text style={pickerStyles.deviceName}>Manual Entry</Text>
                <Text style={pickerStyles.deviceSub}>Type barcode manually</Text>
              </View>
              <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
            </TouchableOpacity>

            {/* Registered devices */}
            {loading && (
              <View style={pickerStyles.loadingRow}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={pickerStyles.loadingText}>Loading devices...</Text>
              </View>
            )}

            {!loading && devices.length > 0 && (
              <>
                <Text style={pickerStyles.sectionLabel}>Registered Devices</Text>
                {devices.map((device) => {
                  const icon = DEVICE_ICONS[device.category] || 'hardware-chip-outline';
                  const sub = device.scannerType ? SCANNER_TYPE_LABELS[device.scannerType] || device.scannerType : '';
                  const statusColor = device.deviceStatus === 'online' ? '#10B981' : device.deviceStatus === 'idle' ? '#F59E0B' : '#6B7280';
                  return (
                    <TouchableOpacity
                      key={device._id}
                      style={pickerStyles.deviceRow}
                      onPress={() => onSelect(device)}
                      activeOpacity={0.7}
                    >
                      <View style={[pickerStyles.deviceIcon, { backgroundColor: colors.primary + '15' }]}>
                        <Ionicons name={icon as any} size={22} color={colors.primary} />
                      </View>
                      <View style={pickerStyles.deviceInfo}>
                        <View style={pickerStyles.deviceNameRow}>
                          <Text style={pickerStyles.deviceName}>{device.name}</Text>
                          <View style={[pickerStyles.statusDot, { backgroundColor: statusColor }]} />
                        </View>
                        <Text style={pickerStyles.deviceSub}>
                          {sub || device.category}
                          {device.location ? ` · ${device.location}` : ''}
                          {device.ipAddress ? ` · ${device.ipAddress}` : ''}
                        </Text>
                      </View>
                      <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                  );
                })}
              </>
            )}

            {!loading && devices.length === 0 && (
              <View style={pickerStyles.emptyDevices}>
                <Text style={pickerStyles.emptyDevicesText}>No registered scanner or camera devices.</Text>
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

export const ProductScannerScreen: React.FC<ProductScannerScreenProps> = ({ catalogueId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, showClickableNotification, activeTab, setActiveTab } = useAppContext();

  // Device picker
  const [showDevicePicker, setShowDevicePicker] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any | null | undefined>(undefined); // undefined = not chosen yet

  // Scan state
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [lastBarcode, setLastBarcode] = useState('');
  const [foundProduct, setFoundProduct] = useState<any>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [CameraView, setCameraView] = useState<any>(null);
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const resultAnim = useRef(new Animated.Value(0)).current;

  // Load scanner/camera devices on mount
  useEffect(() => {
    setDevicesLoading(true);
    apiService.getDevices({ category: 'scanner', limit: 50 }).then((res) => {
      const scanners = res.statusCode === 1000 ? res.data?.devices || [] : [];
      apiService.getDevices({ category: 'camera', limit: 50 }).then((camRes) => {
        const cameras = camRes.statusCode === 1000 ? camRes.data?.devices || [] : [];
        setDevices([...scanners, ...cameras]);
      }).catch(() => setDevices(scanners));
    }).catch(() => setDevices([])).finally(() => setDevicesLoading(false));
  }, []);

  // After device is selected — set up camera if needed
  useEffect(() => {
    if (selectedDevice === undefined) return; // not chosen yet
    if (selectedDevice === null && Platform.OS !== 'web') {
      // device camera chosen
      loadCamera();
    } else if (selectedDevice?._isManual) {
      setShowManual(true);
    }
    // else: network device — show manual / IP-based input
  }, [selectedDevice]);

  const loadCamera = async () => {
    try {
      const mod = await import('expo-camera');
      setCameraView(() => mod.CameraView);
      const { status } = await mod.Camera.requestCameraPermissionsAsync();
      setHasPermission(status === 'granted');
    } catch {
      setHasPermission(false);
      setShowManual(true);
    }
  };

  const handleDeviceSelect = (device: any | null) => {
    setShowDevicePicker(false);
    setSelectedDevice(device);
    setShowManual(device?._isManual === true);
    handleReset();
  };

  const showResult = () => {
    resultAnim.setValue(0);
    Animated.spring(resultAnim, { toValue: 1, useNativeDriver: true, tension: 80 }).start();
  };

  const handleBarcode = async (barcode: string) => {
    if (scanState === 'scanning') return;
    const trimmed = barcode.trim();
    if (!trimmed) return;

    setLastBarcode(trimmed);
    setScanState('scanning');
    setFoundProduct(null);

    try {
      const response = await apiService.getProductsByCatalogue(catalogueId || 'default', {
        barcode: trimmed,
        limit: 1,
      });

      const products = response.data?.products || [];
      if (response.statusCode === 1000 && products.length > 0) {
        const product = products[0];
        setFoundProduct(product);
        setScanState('found');

        showClickableNotification(
          'success',
          'Product Found',
          `Barcode ${trimmed} matches ${product.name || 'a product'}.`,
          {
            label: 'Create Order',
            onPress: () => navigateTo('order-add', undefined, { scannedProductId: product._id || product.id }),
          }
        );

        showNotification('info', 'Product ready', 'Scanned product is ready to be added to order.');
      } else {
        setScanState('not_found');

        showClickableNotification(
          'warning',
          'Product Not Found',
          `Barcode ${trimmed} is not in the catalogue.`,
          {
            label: 'Create Product',
            onPress: () => navigateTo('product-create', catalogueId || 'default'),
          }
        );
      }
      showResult();
    } catch {
      setScanState('error');
      showResult();
    }
  };

  const handleManualSubmit = () => {
    const trimmed = manualInput.trim();
    if (!trimmed) return;
    setManualInput('');
    handleBarcode(trimmed);
  };

  const handleReset = () => {
    setScanState('idle');
    setLastBarcode('');
    setFoundProduct(null);
    setManualInput('');
    resultAnim.setValue(0);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // ─── Result banner ──────────────────────────────────────────────────────────

  const renderResultBanner = () => {
    if (scanState === 'idle') return null;

    if (scanState === 'scanning') {
      return (
        <View style={[styles.resultBanner, styles.resultScanning]}>
          <ActivityIndicator size="small" color={colors.white} />
          <Text style={styles.resultText}>Looking up "{lastBarcode}"...</Text>
        </View>
      );
    }

    return (
      <Animated.View style={[
        styles.resultCard,
        scanState === 'found' ? styles.resultFound : styles.resultNotFound,
        { transform: [{ scale: resultAnim }], opacity: resultAnim },
      ]}>
        {scanState === 'found' && foundProduct && (
          <>
            <View style={styles.resultHeader}>
              <Ionicons name="checkmark-circle" size={28} color={colors.success} />
              <Text style={styles.resultTitle}>Product Found</Text>
            </View>
            <Text style={styles.productName}>{foundProduct.name}</Text>
            {foundProduct.brand && <Text style={styles.productBrand}>{foundProduct.brand}</Text>}
            <Text style={styles.productPrice}>{getCurrencySymbol(foundProduct.currency || businessCurrency())} {foundProduct.price?.toFixed(2)}</Text>
            <View style={styles.resultActions}>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigateTo('product-detail', foundProduct._id || foundProduct.id)}>
                <Ionicons name="eye-outline" size={18} color={colors.white} />
                <Text style={styles.actionBtnText}>Open Product</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionBtn, styles.actionBtnOutline]}
                onPress={() => navigateTo('order-add', undefined, { scannedProductId: foundProduct._id || foundProduct.id })}
              >
                <Ionicons name="cart-outline" size={18} color={colors.primary} />
                <Text style={[styles.actionBtnText, styles.actionBtnTextOutline]}>Create Order</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.resultActions}>
              <TouchableOpacity style={[styles.actionBtn, styles.actionBtnOutline]} onPress={handleReset}>
                <Ionicons name="scan-outline" size={18} color={colors.primary} />
                <Text style={[styles.actionBtnText, styles.actionBtnTextOutline]}>Scan Again</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {scanState === 'not_found' && (
          <>
            <View style={styles.resultHeader}>
              <Ionicons name="search-outline" size={28} color={colors.warning} />
              <Text style={styles.resultTitle}>Product Not Found</Text>
            </View>
            <Text style={styles.resultSubtitle}>Barcode: {lastBarcode}</Text>
            <View style={styles.resultActions}>
              <TouchableOpacity
                style={styles.actionBtn}
                onPress={() => {
                  navigateTo('product-create', catalogueId || 'default', { scannedBarcode: lastBarcode });
                  showNotification('info', 'Barcode Ready', `Barcode "${lastBarcode}" will be pre-filled`);
                }}
              >
                <Ionicons name="add-circle-outline" size={18} color={colors.white} />
                <Text style={styles.actionBtnText}>Create Product</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.actionBtn, styles.actionBtnOutline]} onPress={handleReset}>
                <Ionicons name="close-circle-outline" size={18} color={colors.primary} />
                <Text style={[styles.actionBtnText, styles.actionBtnTextOutline]}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {scanState === 'error' && (
          <>
            <View style={styles.resultHeader}>
              <Ionicons name="alert-circle-outline" size={28} color={colors.error} />
              <Text style={styles.resultTitle}>Error</Text>
            </View>
            <Text style={styles.resultSubtitle}>Failed to look up barcode</Text>
            <TouchableOpacity style={[styles.actionBtn, styles.actionBtnOutline]} onPress={handleReset}>
              <Text style={[styles.actionBtnText, styles.actionBtnTextOutline]}>Try Again</Text>
            </TouchableOpacity>
          </>
        )}
      </Animated.View>
    );
  };

  // ─── Content ────────────────────────────────────────────────────────────────

  const renderChooseDevice = () => (
    <View style={styles.chooseContainer}>
      <Ionicons name="scan-outline" size={72} color={colors.primary} />
      <Text style={styles.chooseTitle}>Ready to Scan</Text>
      <Text style={styles.chooseSubtitle}>Choose how you want to scan the product barcode</Text>

      {Platform.OS !== 'web' && (
        <TouchableOpacity style={styles.chooseBtn} onPress={() => handleDeviceSelect(null)} activeOpacity={0.8}>
          <View style={[styles.chooseBtnIcon, { backgroundColor: '#6366F118' }]}>
            <Ionicons name="camera-outline" size={26} color="#6366F1" />
          </View>
          <View style={styles.chooseBtnText}>
            <Text style={styles.chooseBtnTitle}>Use Device Camera</Text>
            <Text style={styles.chooseBtnSub}>Built-in camera barcode scanner</Text>
          </View>
          <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
        </TouchableOpacity>
      )}

      <TouchableOpacity style={styles.chooseBtn} onPress={() => handleDeviceSelect({ _isManual: true })} activeOpacity={0.8}>
        <View style={[styles.chooseBtnIcon, { backgroundColor: '#F59E0B18' }]}>
          <Ionicons name="pencil-outline" size={26} color="#F59E0B" />
        </View>
        <View style={styles.chooseBtnText}>
          <Text style={styles.chooseBtnTitle}>Enter Manually</Text>
          <Text style={styles.chooseBtnSub}>Type or paste the barcode</Text>
        </View>
        <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
      </TouchableOpacity>

      {/* Registered devices */}
      {devicesLoading ? (
        <View style={styles.devicesLoading}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.devicesLoadingText}>Loading registered devices...</Text>
        </View>
      ) : devices.length > 0 ? (
        <>
          <Text style={styles.devicesSectionLabel}>Registered Devices</Text>
          {devices.map((device) => {
            const icon = (device.category === 'camera' ? 'camera-outline' : 'barcode-outline') as any;
            const statusColor = device.deviceStatus === 'online' ? '#10B981' : device.deviceStatus === 'idle' ? '#F59E0B' : '#6B7280';
            return (
              <TouchableOpacity key={device._id} style={styles.chooseBtn} onPress={() => handleDeviceSelect(device)} activeOpacity={0.8}>
                <View style={[styles.chooseBtnIcon, { backgroundColor: colors.primary + '18' }]}>
                  <Ionicons name={icon} size={26} color={colors.primary} />
                </View>
                <View style={styles.chooseBtnText}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text style={styles.chooseBtnTitle}>{device.name}</Text>
                    <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
                  </View>
                  <Text style={styles.chooseBtnSub}>
                    {device.scannerType ? SCANNER_TYPE_LABELS[device.scannerType] : device.category}
                    {device.location ? ` · ${device.location}` : ''}
                  </Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
              </TouchableOpacity>
            );
          })}
        </>
      ) : null}
    </View>
  );

  const renderManual = () => (
    <View style={styles.manualContainer}>
      <View style={styles.manualCard}>
        {selectedDevice && !selectedDevice._isManual && (
          <View style={styles.deviceBadge}>
            <Ionicons name="hardware-chip-outline" size={14} color={colors.primary} />
            <Text style={styles.deviceBadgeText}>{selectedDevice.name}</Text>
          </View>
        )}
        <Ionicons name="barcode-outline" size={56} color={colors.primary} />
        <Text style={styles.manualTitle}>Enter Barcode</Text>
        <TextInput
          style={styles.manualInput}
          value={manualInput}
          onChangeText={setManualInput}
          placeholder="e.g. 1234567890123"
          placeholderTextColor={colors.textLight}
          keyboardType="number-pad"
          autoFocus
          onSubmitEditing={handleManualSubmit}
          returnKeyType="search"
        />
        <TouchableOpacity
          style={[styles.actionBtn, !manualInput.trim() && styles.actionBtnDisabled]}
          onPress={handleManualSubmit}
          disabled={!manualInput.trim()}
        >
          <Ionicons name="search-outline" size={18} color={colors.white} />
          <Text style={styles.actionBtnText}>Search</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.switchBtn} onPress={() => { setSelectedDevice(undefined); setShowManual(false); handleReset(); }}>
          <DirectionalIcon name="arrow-back-outline" size={16} color={colors.primary} />
          <Text style={styles.switchBtnText}>Change Scanner</Text>
        </TouchableOpacity>
      </View>
      {renderResultBanner()}
    </View>
  );

  const renderNetworkDevice = () => (
    <View style={styles.manualContainer}>
      <View style={styles.manualCard}>
        <View style={styles.deviceBadge}>
          <Ionicons name="barcode-outline" size={14} color={colors.primary} />
          <Text style={styles.deviceBadgeText}>{selectedDevice?.name}</Text>
        </View>
        <Ionicons name="barcode-outline" size={56} color={colors.primary} />
        <Text style={styles.manualTitle}>Scan with {selectedDevice?.name}</Text>
        <Text style={styles.manualHint}>
          Scan the barcode using the device. If the device sends data via network, enter the result below:
        </Text>
        <TextInput
          style={styles.manualInput}
          value={manualInput}
          onChangeText={setManualInput}
          placeholder="Barcode will appear here"
          placeholderTextColor={colors.textLight}
          autoFocus
          onSubmitEditing={handleManualSubmit}
          returnKeyType="search"
        />
        <TouchableOpacity
          style={[styles.actionBtn, !manualInput.trim() && styles.actionBtnDisabled]}
          onPress={handleManualSubmit}
          disabled={!manualInput.trim()}
        >
          <Ionicons name="search-outline" size={18} color={colors.white} />
          <Text style={styles.actionBtnText}>Search</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.switchBtn} onPress={() => { setSelectedDevice(undefined); handleReset(); }}>
          <DirectionalIcon name="arrow-back-outline" size={16} color={colors.primary} />
          <Text style={styles.switchBtnText}>Change Scanner</Text>
        </TouchableOpacity>
      </View>
      {renderResultBanner()}
    </View>
  );

  const renderCamera = () => {
    if (hasPermission === null || !CameraView) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Initializing camera...</Text>
        </View>
      );
    }

    if (hasPermission === false) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="camera-off-outline" size={64} color={colors.error} />
          <Text style={styles.errorText}>Camera permission required</Text>
          <TouchableOpacity style={styles.switchBtn} onPress={() => { setShowManual(true); handleReset(); }}>
            <Ionicons name="pencil-outline" size={18} color={colors.primary} />
            <Text style={styles.switchBtnText}>Enter manually</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.cameraWrapper}>
        <CameraView
          style={StyleSheet.absoluteFill}
          facing="back"
          barcodeScannerSettings={{ barcodeTypes: ['ean13', 'ean8', 'qr', 'code128', 'code39', 'upc_a', 'upc_e', 'itf14'] }}
          onBarcodeScanned={scanState === 'idle' ? ({ data }: { data: string }) => handleBarcode(data) : undefined}
        />
        {scanState === 'idle' && (
          <View style={styles.cameraOverlay} pointerEvents="none">
            <Text style={styles.overlayHint}>Point camera at a barcode</Text>
            <View style={styles.scanFrame}>
              <View style={[styles.corner, styles.cornerTL]} />
              <View style={[styles.corner, styles.cornerTR]} />
              <View style={[styles.corner, styles.cornerBL]} />
              <View style={[styles.corner, styles.cornerBR]} />
            </View>
          </View>
        )}
        <View style={styles.resultOverlay}>{renderResultBanner()}</View>
        <TouchableOpacity style={styles.manualTrigger} onPress={() => { setShowManual(true); handleReset(); }}>
          <Ionicons name="pencil-outline" size={16} color={colors.white} />
          <Text style={styles.manualTriggerText}>Manual</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.changeScannerBtn} onPress={() => { setSelectedDevice(undefined); setCameraView(null); setHasPermission(null); handleReset(); }}>
          <Ionicons name="swap-horizontal-outline" size={16} color={colors.white} />
          <Text style={styles.manualTriggerText}>Change</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderContent = () => {
    // Not chosen yet — show device picker
    if (selectedDevice === undefined) {
      return renderChooseDevice();
    }

    // Manual entry or non-camera device
    if (showManual || selectedDevice?._isManual) {
      return renderManual();
    }

    // Network/registered device (non-camera) → manual input with device context
    if (selectedDevice && !selectedDevice._isManual) {
      return renderNetworkDevice();
    }

    // Device camera (selectedDevice === null)
    if (Platform.OS === 'web') {
      return renderManual();
    }
    return renderCamera();
  };

  return (
    <ScreenLayout
      header={
        <Header
          showBackButton={true}
          title="Product Scanner"
          onBackPress={() => navigateTo('products')}
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {renderContent()}
      </View>

      <DevicePicker
        visible={showDevicePicker}
        onClose={() => setShowDevicePicker(false)}
        onSelect={handleDeviceSelect}
        devices={devices}
        loading={devicesLoading}
      />
    </ScreenLayout>
  );
};

const FRAME_SIZE = 240;

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },

  // ─── Device choose ────────────────────────────────────────────────────────
  chooseContainer: {
    flex: 1,
    padding: spacing.lg,
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: spacing.xxl,
  },
  chooseTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  chooseSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    textAlign: 'center',
    fontFamily: typography.fontFamily.regular,
    marginBottom: spacing.sm,
  },
  chooseBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    width: '100%',
    ...designTokens.shadows.sm,
  },
  chooseBtnIcon: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chooseBtnText: { flex: 1 },
  chooseBtnTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  chooseBtnSub: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  devicesLoading: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  devicesLoadingText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  devicesSectionLabel: {
    alignSelf: 'flex-start',
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    color: colors.textLight,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: spacing.sm,
  },

  // ─── Camera ────────────────────────────────────────────────────────────────
  cameraWrapper: { flex: 1, position: 'relative', backgroundColor: '#000' },
  cameraOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)',
    gap: spacing.lg,
  },
  overlayHint: { color: colors.white, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.medium, marginBottom: spacing.md },
  scanFrame: { width: FRAME_SIZE, height: FRAME_SIZE * 0.55, position: 'relative' },
  corner: { position: 'absolute', width: 24, height: 24, borderColor: colors.white },
  cornerTL: { top: 0, left: 0, borderTopWidth: 3, borderLeftWidth: 3 },
  cornerTR: { top: 0, right: 0, borderTopWidth: 3, borderRightWidth: 3 },
  cornerBL: { bottom: 0, left: 0, borderBottomWidth: 3, borderLeftWidth: 3 },
  cornerBR: { bottom: 0, right: 0, borderBottomWidth: 3, borderRightWidth: 3 },
  resultOverlay: { position: 'absolute', bottom: 100, left: spacing.md, right: spacing.md },
  manualTrigger: {
    position: 'absolute', bottom: spacing.xl, left: spacing.xl,
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: designTokens.borderRadius.md,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)',
  },
  changeScannerBtn: {
    position: 'absolute', bottom: spacing.xl, right: spacing.xl,
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: designTokens.borderRadius.md,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)',
  },
  manualTriggerText: { color: colors.white, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium },

  // ─── Manual / network device ───────────────────────────────────────────────
  manualContainer: { flex: 1, padding: spacing.lg, gap: spacing.lg },
  manualCard: {
    backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg,
    padding: spacing.xl, alignItems: 'center', gap: spacing.md,
    ...designTokens.shadows.sm,
  },
  deviceBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs / 2,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: colors.primary + '15',
  },
  deviceBadgeText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
  manualTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  manualHint: { fontSize: typography.fontSize.sm, color: colors.textLight, textAlign: 'center', fontFamily: typography.fontFamily.regular },
  manualInput: {
    width: '100%', borderWidth: 1.5, borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.md,
    fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.regular,
    color: colors.textDark, textAlign: 'center',
  },
  switchBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm },
  switchBtnText: { color: colors.primary, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium },

  // ─── Results ───────────────────────────────────────────────────────────────
  resultBanner: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: designTokens.borderRadius.md },
  resultScanning: { backgroundColor: colors.primary },
  resultText: { color: colors.white, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium },
  resultCard: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.lg, gap: spacing.sm, ...designTokens.shadows.md },
  resultFound: { borderLeftWidth: 4, borderLeftColor: colors.success },
  resultNotFound: { borderLeftWidth: 4, borderLeftColor: colors.warning },
  resultHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  resultTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  resultSubtitle: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  productName: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  productBrand: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  productPrice: { fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.primary, fontFamily: typography.fontFamily.bold },
  resultActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  actionBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: spacing.xs, backgroundColor: colors.primary,
    paddingVertical: spacing.sm, borderRadius: designTokens.borderRadius.md,
  },
  actionBtnOutline: { backgroundColor: 'transparent', borderWidth: 1.5, borderColor: colors.primary },
  actionBtnDisabled: { opacity: 0.4 },
  actionBtnText: { color: colors.white, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  actionBtnTextOutline: { color: colors.primary },

  // ─── Misc ──────────────────────────────────────────────────────────────────
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: spacing.md, backgroundColor: colors.lightGray },
  centerText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  errorText: { fontSize: typography.fontSize.base, color: colors.error, fontFamily: typography.fontFamily.medium },
});

// ─── Device Picker Bottom Sheet Styles ───────────────────────────────────────

const getPickerStyles = () => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 32,
  },
  handle: { width: 40, height: 4, borderRadius: 2, backgroundColor: colors.border, alignSelf: 'center', marginTop: 12, marginBottom: 4 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingVertical: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  closeBtn: { width: 36, height: 36, justifyContent: 'center', alignItems: 'center' },
  list: { paddingHorizontal: spacing.lg, paddingVertical: spacing.md, gap: spacing.sm },
  sectionLabel: {
    fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.textLight,
    fontFamily: typography.fontFamily.semibold, textTransform: 'uppercase', letterSpacing: 0.5,
    marginTop: spacing.sm,
  },
  deviceRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border + '60',
  },
  deviceIcon: { width: 44, height: 44, borderRadius: designTokens.borderRadius.md, justifyContent: 'center', alignItems: 'center' },
  deviceInfo: { flex: 1 },
  deviceNameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  deviceName: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  deviceSub: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: 2 },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  loadingRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.md },
  loadingText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  emptyDevices: { paddingVertical: spacing.md },
  emptyDevicesText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular, textAlign: 'center' },
});
